import React from "react";
import cn from "clsx";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { BonusReward } from "types/playerBonuses";
import {
  clampWagerProgress,
  EMPTY_VALUE,
  formatRewardsShortList,
  getBonusStatusLabel,
} from "helpers/playerBonusFormatters";
import { formatTime, TimeFormat } from "helpers/timeFormat";

import styles from "./ListBonusesTab.module.scss";

interface SplitColumnHeaderProps {
  top: string;
  bottom: string;
}

export const SplitColumnHeader: React.FC<SplitColumnHeaderProps> = ({
  top,
  bottom,
}) => (
  <div className={cn(styles.stackedText, styles.stackedHeader)}>
    <span>{top}</span>
    <span>{bottom}</span>
  </div>
);

interface BonusDateCellProps {
  date?: string | null;
  timeFormat: TimeFormat;
}

export const BonusDateCell: React.FC<BonusDateCellProps> = ({
  date,
  timeFormat,
}) => {
  if (!date) {
    return <span>{EMPTY_VALUE}</span>;
  }

  const parsedDate = moment(date);

  return (
    <div className={cn(styles.stackedText, styles.stackedValue)}>
      <span>{parsedDate.format("DD.MM.YYYY")}</span>
      <span>{formatTime(date, timeFormat)}</span>
    </div>
  );
};

interface BonusStatusBadgeProps {
  status?: string | null;
  historyType?: string;
}

const bonusStatusClassMap: Record<string, string> = {
  queued: "badge--warning",
  activating: "badge--info",
  claimed: "badge--info",
  rewarding: "badge--info",
  wagering: "badge--high",
  completed: "badge--success",
  canceled: "badge--danger",
  cancelled: "badge--danger",
  expired: "badge--neutral",
};

export const BonusStatusBadge: React.FC<BonusStatusBadgeProps> = ({
  status,
  historyType,
}) => {
  const { t } = useTranslation();

  if (!status) {
    return <span>{EMPTY_VALUE}</span>;
  }

  const statusKey = status.toLowerCase();

  return (
    <span
      className={cn(
        "badge",
        bonusStatusClassMap[statusKey] ?? `badge--${statusKey}`,
      )}
    >
      {getBonusStatusLabel(status, historyType, t)}
    </span>
  );
};

interface BonusProgressCellProps {
  wagerProgress?: number | null;
}

export const BonusProgressCell: React.FC<BonusProgressCellProps> = ({
  wagerProgress,
}) => {
  const progress = clampWagerProgress(wagerProgress);

  if (progress === null) {
    return <span>{EMPTY_VALUE}</span>;
  }

  const progressClass =
    progress >= 100
      ? styles.progressBarFull
      : progress <= 0
        ? styles.progressBarEmpty
        : styles.progressBarPartial;

  return (
    <div className={styles.progressCell}>
      <span className={styles.progressPercent}>{progress}%</span>
      <div className={styles.progressBarContainer}>
        <div
          className={cn(styles.progressBar, progressClass)}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

interface BonusRewardCellProps {
  rewards: BonusReward[];
}

export const BonusRewardCell: React.FC<BonusRewardCellProps> = ({
  rewards,
}) => {
  const { t } = useTranslation();

  if (!rewards.length) {
    return <span>{EMPTY_VALUE}</span>;
  }

  const items = formatRewardsShortList(rewards, t);

  return (
    <ul className={styles.rewardList}>
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
};
